import { z } from 'zod';
import { HttpClient } from '@sisense/sdk-rest-client';
import {
	Dataset,
	QueryExecutionResult,
	QueryState,
} from '../../services/nlq/nlq-service';

export interface QueryResult {
	queryId: string;
	title: string;
	nlqResult: QueryExecutionResult;
}

/**
 * Canonical tool description for buildQuery — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const buildQueryToolDescription = `Runs an analytics query from a natural language prompt and returns the results as structured data (tabular exploration, no chart).

**New query** — Only for the first query in a chain, or when the user clearly abandons the prior result and starts unrelated analysis. Omit \`priorQueryId\` (or null). Put the full natural-language intent in \`queryPrompt\`.

**Revise an existing query** — Use for same-thread follow-ups that adjust the *same* analysis: different breakdown (e.g. by year, by category), added or removed filters, sort order, measure swaps, limits (Top N), trend/forecast, etc. Treat these as revisions, not separate unrelated queries.

For revisions: 
- Set \`priorQueryId\` to the exact string in the \`queryId\` field from the **latest successful** tool result of this tool in the conversation (that value is the same id you pass as \`queryId\` when wiring a prior tabular result into the chart tool). 
- In \`queryPrompt\`, pass only the delta vs. that prior query — do not repeat the full prior specification.

Return value includes queryId, queryState, and dataset rows. If the dataset includes a "sample" key, the full result was size-limited — call the chart tool with that queryId so the user can explore the complete result in the chart widget.`;

/**
 * Schema for buildQuery engine parameters
 */
export const buildQuerySchema = z.object({
	dataSourceTitle: z
		.string()
		.describe('The title of the data source to query'),
	queryPrompt: z
		.string()
		.describe(
			'What to retrieve. New query: full intent. Revision (priorQueryId set): only the change vs. the prior result — not a restated full query.',
		),
	priorQueryId: z
		.string()
		.nullable()
		.optional()
		.describe(
			'Exact queryId string from the latest successful result of this tool in the thread when revising that query (breakdown, filter, sort, etc.). Omit or set to null for a brand-new query or an unrelated analysis.',
		),
});

/**
 * Context interface for buildQueryEngine
 */
export interface BuildQueryContext {
	/** Tool call ID for tracking */
	toolCallId: string;
	/** HTTP client for API calls (optional, uses singleton if not provided) */
	httpClient?: HttpClient;
	/** Common filters (e.g., from dashboard filters) to be applied to the query. */
	commonFilters?: unknown;
	/**
	 * Resolve a stored query by id when `params.priorQueryId` is set.
	 * Required whenever `priorQueryId` is present; prior NLQ state is read from
	 * `nlqResult.queryState` only.
	 */
	retrieveQuery?: (queryId: string) => QueryResult | null;
	/**
	 * When true (default), prior NLQ state is sent as REST `chartState`.
	 * When false, prior state is inlined into `text` instead (instances without wire delta support).
	 */
	isNlqDeltaEnabled?: boolean;
}

/** Summary of a query for LLM consumption (tool response and prompt context). */
export interface QuerySummary {
	queryId: string;
	title: string;
	queryState: QueryState;
	dataset: Dataset;
}

/**
 * Converts a QueryResult into a summary for LLM consumption.
 * @param result - The query result
 * @param includeDataset - Whether to include the full dataset (enables natural response). When false, returns a directive to build a chart instead.
 */
export function toQuerySummary(
	result: QueryResult,
	includeDataset = true,
): QuerySummary {
	return {
		queryId: result.queryId,
		title: result.title,
		queryState: result.nlqResult.queryState,
		dataset: includeDataset
			? result.nlqResult.dataset!
			: 'Query executed successfully. Call buildChart with this queryId to visualize the results.',
	};
}
